/**
 * Reduced motion, as state rather than as a stylesheet-only concern.
 *
 * `tokens.css` already collapses the motion durations under
 * `prefers-reduced-motion: reduce`, which covers transitions. It cannot cover
 * behaviour: an auto-advancing carousel, a scroll-into-view animation, or a
 * JS-driven transport still needs to know. Native nils the animation out
 * entirely rather than shortening it, so consumers branch rather than scale.
 */

import type { MatchMedia, MediaProbeResult } from "./mediaQuery";
import { useMediaProbe } from "./mediaQuery";
import { useLayoutPreferences } from "./preferences";

export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export function probeReducedMotion(matchMedia: MatchMedia): MediaProbeResult<boolean> {
  return {
    value: matchMedia(REDUCED_MOTION_QUERY).matches,
    queries: [REDUCED_MOTION_QUERY],
  };
}

/**
 * `SystemOverridablePreference` is `"system" | "on"` and has no `"off"` arm on
 * purpose: a user who asked the operating system for less motion must not have
 * that overridden by an app-level toggle, so the in-app control can only add
 * the reduction, never remove it.
 */
export function useReducedMotion(): boolean {
  const { reduceMotion } = useLayoutPreferences();
  const detected = useMediaProbe(probeReducedMotion, false);
  return reduceMotion === "on" ? true : detected;
}
